import EditIcon from "@mui/icons-material/edit";
import { Button, IconButton, Link, Stack, Typography } from "@mui/material";
import axios from "axios";
import { MRT_ColumnDef } from "material-react-table";
import { useState } from "react";
import { useSnackbar } from "notistack";
import Iconify from "../../../../minimals/components/iconify";
import DownloadFileButton from "../../../recordables/DownloadFileButton";
import { convertToFileDownload } from "../../../utils/convertToFileDownload";
import { Policy } from "../policyIssues/types";
import { downloadPolicyDocuments } from "./downloadPolicyDocuments";
import { AutocompleteFilter, createAutocompleteFilterFn, createCellRenderer } from "../../generateAutocompleteColumnAndFilter";

export const getColumns = (
  handleOpenModal: (selectedPolicy: Policy) => void,
  setSelectedPolicy: (selectedPolicy: Policy) => void,
  handleOpenAssignedTo: (seletedPolicy: Policy) => void
): MRT_ColumnDef<Policy>[] => {
  const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;
  return [
    // {
    //   id: "notes",
    //   header: "",
    //   enableColumnFilter: false,
    //   enableColumnActions: false,
    //   enableColumnDragging: false,
    //   enableColumnFilterModes: false,
    //   size: 50,
    //   enableHiding: false
    // },
    {
      header: "Department",
      id: "dept",
      accessorFn: (row) => row.department || "",
      filterFn: createAutocompleteFilterFn<Policy>("department"),
      Filter: ({ table, column }) => (
        <AutocompleteFilter table={table} column={column} accessorPath="department" placeholder="Department" />
      ),
      Cell: createCellRenderer<Policy>("department"),
      size: 120,
      muiFilterTextFieldProps: {
        variant: "outlined",
        size: "small"
      }
    },
    {
      header: "Policy Number",
      accessorKey: "policyNumber",
      id: "policyNumber",
      Cell: ({ row }) => <Typography variant="body2">{row.original.policyNumber}</Typography>
    },
    {
      header: "Order Number",
      accessorKey: "orderNumber",
      id: "orderNumber",
      Cell: ({ row }) => (
        <Link href={`${momentumUrl}new-home/${row.original.orderNumber}`} target="_blank">
          <Typography variant="body2">{row.original.orderNumber}</Typography>
        </Link>
      )
    },
    {
      header: "Policy Type",
      accessorKey: "type",
      filterVariant: "select",
      filterSelectOptions: [
        { label: "Owners", value: 1 },
        { label: "Loan", value: 2 }
      ],
      muiFilterTextFieldProps: {
        variant: "outlined",
        size: "small",
        select: true
      },
      Cell: ({ row }) => {
        const [loading, setLoading] = useState(false);
        const { enqueueSnackbar } = useSnackbar();
        const generatePolicyEmail = async (selectedUploadPolicy: Policy) => {
          setLoading(true);
          try {
            const { data } = await axios.post("/api/outstandingChecks/GeneratePolicyEmail", selectedUploadPolicy, {
              headers: {
                "Content-Type": "application/json"
              }
            });
            if (!data) {
              enqueueSnackbar("The policy document was not found. It may have been deleted. Please check that the document exists.", { variant: "error", anchorOrigin: { vertical: "top", horizontal: "center" } });
            } else {
              convertToFileDownload(data.base64Email, `${data.subject}.msg`);
            }
          } finally {
            setLoading(false);
          }
        };
        return (
          <Stack direction={"row"} alignItems={"center"}>
            <Typography variant="body2" sx={{ width: 60 }}>
              {+row.original.type === 2 ? "Loan " : "Owners "}
              {row.original.position && row.original.position}
            </Typography>
            {row.original.status !== 0 ? (
              <>
                <IconButton
                  onClick={() => downloadPolicyDocuments(row.original)}
                  disabled={row.original.associatedDocuments?.length === 0 && !row.original.documentId}
                >
                  <Iconify icon="eva:download-fill" />
                </IconButton>
                <IconButton>
                  <Iconify
                    icon={
                      loading
                        ? "line-md:loading-twotone-loop"
                        : row.original.note
                        ? "fluent:chat-mail-20-regular"
                        : "eva:email-outline"
                    }
                    sx={{ width: 24, height: 24 }}
                    onClick={() => {
                      generatePolicyEmail(row.original);
                    }}
                  />
                </IconButton>
              </>
            ) : (
              <div />
            )}
          </Stack>
        );
      }
    },
    {
      header: "Due",
      accessorKey: "due",
      id: "due",
      accessorFn: (row) => row.assignedToUser?.fullName,
      Cell: ({ row }) => {
        const taskReceivedDate = row.original.taskReceivedDate;
        let dueDateDisplay = "";
        if (taskReceivedDate) {
          const date = new Date(taskReceivedDate);
          if (!isNaN(date.getTime())) {
            const dueDate = new Date(date);
            dueDate.setDate(dueDate.getDate() + 30);
            dueDateDisplay = `${dueDate.getMonth() + 1}/${dueDate.getDate()}/${dueDate.getFullYear()}`;
          }
        }
        return <Typography variant="body2">{dueDateDisplay}</Typography>;
      },
      muiFilterTextFieldProps: {
        variant: "outlined",
        size: "small"
      }
    },
    {
      header: "Assigned to",
      id: "assignedTo",
      accessorFn: (row) => row.assignedToSendUser?.fullName || row.assignedToSendName,
      Cell: ({ row }) => (
        <Stack direction="row" alignItems="center">
          <Typography variant="body2" width={140}>
            {row.original?.assignedToSendUser?.fullName || row.original?.assignedToSendName}
          </Typography>
          <IconButton onClick={() => handleOpenAssignedTo(row.original)} aria-label="edit">
            <EditIcon />
          </IconButton>
        </Stack>
      ),
      muiFilterTextFieldProps: {
        variant: "outlined",
        size: "small"
      }
    },
    {
      header: "Status",
      accessorKey: "status",
      id: "status",
      filterVariant: "select",
      muiFilterTextFieldProps: {
        variant: "outlined",
        size: "small"
      },
      accessorFn: (row) => row.status,
      filterSelectOptions: [
        { text: "Ready to Send", value: 2 },
        { text: "Sent", value: 3 }
      ],
      Cell: ({ row }) => (
        <div>
          {row.original?.status && +row.original?.status === 3 ? (
            <Stack direction={"row"} alignItems={"center"}>
              <DownloadFileButton fileId={row.original.transmittedDocumentId} />
              <Typography>Sent</Typography>
            </Stack>
          ) : (
            <Button
              onClick={() => {
                handleOpenModal(row.original);
                setSelectedPolicy(row.original);
              }}
            >
              Upload Transmittal
            </Button>
          )}
        </div>
      )
    }
  ];
};
